/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import {
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  ScrollArea,
  ThemeProvider,
} from "@packages/ui";
import { DataTestId } from "@tools/playwright/global/types/enums/dataTestId.enum";
import type { RefObject, UIEvent } from "react";
import { useMemo } from "react";

import { useTranscriptDisplay } from "./TranscriptSettingsContext";

const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  placeContent: "flex-start",
  placeItems: "center",
  position: "relative",
};
const colorModeStyle: Interpolation<string> = (bgColor) => ({
  backgroundColor: bgColor,
  color: constantSemanticColors.onColorHighest,
});
const colorModeScrollbarStyle: Interpolation = {
  borderLeftColor: colors.transparent,
};
const scrollAreaStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  height: "100vh",
  transitionDuration: "350ms",
  transitionProperty: "background-color, color",
  transitionTimingFunction: "ease-in-out",
  width: "100vw",
});
const maskStyle: Interpolation = {
  position: "absolute",
  transition: "top 250ms ease-out, background 350ms ease-in-out",
};
const hideScrollbarStyle: Interpolation = {
  msOverflowStyle: "none",
  opacity: 0,
  scrollbarWidth: "none",
  transition: "opacity 250ms ease-out",
  "::-webkit-scrollbar": {
    display: "none",
  },
};

const maskStyles: Record<string, Interpolation> = {
  expanded: {
    top: "229px",
    [cssMediaQueryMinWidthTablet]: { top: "270px" },
  },
  collapsed: {
    top: "106px",
  },
};

interface TranscriptContainerProps {
  isAutoScrolling: boolean;
  isHeaderCollapsed: boolean;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
  scrollContainerRef: RefObject<HTMLDivElement>;
}

export const TranscriptContainer = ({
  isAutoScrolling,
  isHeaderCollapsed,
  onScroll,
  children,
  scrollContainerRef,
}: TranscriptContainerProps) => {
  const { theme: themeType, colorHex } = useTranscriptDisplay();

  const maskTopCSS = useMemo(
    () => [maskStyle, maskStyles[isHeaderCollapsed ? "collapsed" : "expanded"]],
    [isHeaderCollapsed],
  );

  const masksColor = useMemo(() => {
    return themeType === "color"
      ? (colorHex as `#${string}`)
      : themeType === "dark"
        ? "blackOLED"
        : "white";
  }, [themeType, colorHex]);

  const coreCSS = useMemo(
    () => [
      scrollAreaStyle,
      themeType === "color" ? colorModeStyle(colorHex) : null,
    ],
    [themeType, colorHex],
  );

  const scrollbarCSS = useMemo(
    () => [
      themeType === "color"
        ? [colorModeStyle(colorHex), colorModeScrollbarStyle]
        : null,
      isAutoScrolling && hideScrollbarStyle,
    ],
    [themeType, colorHex, isAutoScrolling],
  );

  return (
    <ThemeProvider
      forceTheme={themeType === "light" ? "light" : "dark"}
      as={null}
    >
      <ScrollArea
        viewportProps={{
          onScroll,
        }}
        maskBottom
        maskTop
        maskTopCSS={maskTopCSS}
        masksColor={masksColor}
        maskBottomCSS={maskStyle}
        css={coreCSS}
        scrollbarVerticalCSS={scrollbarCSS}
        data-testid={DataTestId.transcriptContainer}
        ref={scrollContainerRef}
      >
        <div css={containerStyle}>{children}</div>
      </ScrollArea>
    </ThemeProvider>
  );
};
